import React, { useCallback, useRef, useState } from "react";
import PaginationDots from "../PaginationDots";
import Typography from "../Typography";
import BrandTypography from "../BrandTypography";
import useIsIntersectingWithRoot from "../../hooks/useIsIntersectingWithRoot";

import styles from "./thumbnailGallery.module.scss";

type Props = {
  images: Array<any>,
  linkComponent: (href: string, children: any) => void
};

type ImageProps = {
  title: string,
  description: string,
  src: string,
  root: any,
  onVisible: () => void,
  index: number,
  active: boolean,
  className: string
};

const Image = ({
  title,
  description,
  src,
  root,
  onVisible,
  index,
  active,
  className
}: ImageProps) => {
  const ref = useRef();

  const onIntersecting = useCallback(() => {
    onVisible(index);
  }, [onVisible, index]);

  useIsIntersectingWithRoot(ref, root, 0.51, onIntersecting);

  const activeClass = active ? styles.imageActive : "";

  return (
    <div className={`${className} ${activeClass}`} ref={ref}>
      <img src={src} alt={title} className={styles.image} loading="lazy" />
      <div className={styles.content}>
        <BrandTypography color="primary-inverse" bold>
          {title}
        </BrandTypography>
        <Typography className={styles.description} color="primary-inverse">
          {description}
        </Typography>
      </div>
    </div>
  );
};

const ThumbnailGallery = ({ images, linkComponent }: Props) => {
  const imageCount = images.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const ref = useRef();

  const onVisible = useCallback(
    (index) => {
      setCurrentIndex(index);
    },
    [setCurrentIndex]
  );

  const scrollItemIntoView = (index) => {
    const width = ref.current.children[index].clientWidth;
    const offset = index * width;

    ref.current.scrollLeft = offset;
  };

  return (
    <div>
      <div className={styles.grid} ref={ref}>
        {images.map(({ title, description, url, href }, index) =>
          linkComponent(
            href,
            <Image
              title={title}
              description={description}
              src={url}
              root={ref}
              onVisible={onVisible}
              index={index}
              className={`${styles.aspectRatioSquare} ${styles.activeImage}`}
            />
          )
        )}
      </div>
      <div className={styles.paginationImages}>
        {images.map(({ title, description, url }, index) => {
          return (
            <button type="button" onClick={() => scrollItemIntoView(index)}>
              <Image
                title={title}
                description={description}
                src={url}
                root={ref}
                onVisible={onVisible}
                index={index}
                active={currentIndex === index}
                className={styles.thumbnailImage}
              />
            </button>
          );
        })}
      </div>
      <div className={styles.paginationDots}>
        <PaginationDots
          count={imageCount}
          active={currentIndex}
          onClick={scrollItemIntoView}
          theme="brand"
          size="large"
          equalSizes
        />
      </div>
    </div>
  );
};

export default ThumbnailGallery;
